import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CrmToaster, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { apiGet, apiPost } from '../../utils/api'
import {
  addActivity, addTask, getDealById, getOnboarding, getUsers, moveDealStage, obAddHours, obArchive, obFiles, obGetLink, obMarkPaid, obSetRisk,
  obUpdateMeeting, updateTask, type Onboarding,
} from './crmApi'
import { STAGE_MAP } from './crmData'
import { ObPathwaysTab } from './ObPathwaysTab'
import { DealEmails } from './DealEmails'
import { DealWhatsApp } from './DealWhatsApp'
import { DealConvoHistory } from './DealConvoHistory'
import { DealSupport } from './DealSupport'
import { DealPlatform } from './DealPlatform'
import { DealUsers } from './DealUsers'
import { DealQualification } from './DealQualification'
import { BookingModal } from './BookingModal'
import { Card, Chip, Eyebrow, Pill, BtnPrimary, BtnSoft, BtnOutline, UTab, OB, PATHWAY_COLORS, personColor, useObFont, fmtDay, fmtDayTime } from './obDesign'

/**
 * ONBOARDINGS MODULE · COMPANY PAGE — rebuilt to Natalie's prototype
 * mockups (Company.dc.html chrome + Overview; CoPathways/CoPathways2 for
 * the Pathways tab) after Lee's 30 Oct review. 11 tabs per pack §2.2.
 * Tabs not yet restyled to their mockups keep their working bodies and
 * follow in the next deploy.
 */

type Tab = 'overview' | 'todo' | 'pathways' | 'sessions' | 'notes' | 'platform' | 'comms' | 'team' | 'workflow' | 'plan' | 'details'
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: 'Overview' }, { key: 'todo', label: 'To-do' }, { key: 'pathways', label: 'Pathways' },
  { key: 'sessions', label: 'Sessions' }, { key: 'notes', label: 'Notes & activity' }, { key: 'platform', label: 'Platform activity' },
  { key: 'comms', label: 'Emails & chats' }, { key: 'team', label: 'Team' }, { key: 'workflow', label: 'Workflow and process' },
  { key: 'plan', label: 'Plan & payments' }, { key: 'details', label: 'Details' },
]

const SCROLL: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 32 }

interface TodoRow { key: string; taskId: string | null; type: string; side: 'ours' | 'theirs'; title: string; origin: string | null; company: string; leadId: string | null; ownerName: string | null; dueAt: string | null; overdueDays: number | null; action: string }

const pathwayOfShort = (short: string): string =>
  /crm/i.test(short) ? 'Data migration' : /bespoke/i.test(short) ? 'Proposal set-up' : 'Training & Setup'

export const CrmObCompanyPage: React.FC = () => {
  useObFont()
  const { id = '' } = useParams()
  const [sp, setSp] = useSearchParams()
  const navigate = useNavigate()
  const [ob, setOb] = useState<(Onboarding & Record<string, any>) | null>(null)
  const [deal, setDeal] = useState<any>(null)
  const [tab, setTab] = useState<Tab>((sp.get('tab') as Tab) || 'overview')
  const [bookOpen, setBookOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  useEffect(() => { getUsers().then((u: any) => setUsers(u.users || u)).catch(() => {}) }, [])

  const load = () => {
    getOnboarding(id).then((o: any) => {
      setOb(o)
      if (o.leadId) getDealById(o.leadId).then(setDeal).catch(() => setDeal(null))
    }).catch(() => crmToast('Could not load the onboarding', 'error'))
  }
  useEffect(() => { setOb(null); setDeal(null); load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sp.get('tab') !== tab) { sp.set('tab', tab); setSp(sp, { replace: true }) } }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!ob) return <div style={{ padding: 24, fontSize: 12.5, color: OB.muted, fontFamily: OB.font }}>Loading…</div>
  const meta = ob.stagingMeta || {}
  const leadId = ob.leadId || null
  const ownerName = users.find((u) => String(u.id) === String(ob.ownerId))?.name || 'Unassigned'
  const over = ob.hoursAllowance ? Math.max(0, ob.hoursUsed - ob.hoursAllowance) : 0

  const ensureDraft = (kind: string, why: string) =>
    apiPost('/crm/ob-drafts/ensure', { kind, onboardingId: ob.id, leadId, why })
      .then(async (r: Response) => { const d: any = await r.json(); navigate(`/crm/onboardings/followups?draft=${d.id}`) })
      .catch(() => crmToast('Could not raise the draft', 'error'))

  const topUp = () => {
    const h = window.prompt('Top up hours — how many?')
    const n = Number(h)
    if (!h || !Number.isFinite(n) || n <= 0) return
    obAddHours(ob.id, n, 'Top-up from the company page').then(() => { crmToast(`+${n}h added`, 'success'); load() }).catch(() => crmToast('Could not add hours', 'error'))
  }
  const customerLink = (open = false) => obGetLink(ob.id).then(({ url }) => {
    if (open) window.open(url, '_blank', 'noopener')
    else navigator.clipboard.writeText(url).then(() => crmToast('Customer link copied', 'success'))
  }).catch(() => crmToast('No customer link yet', 'error'))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: OB.pageBg, fontFamily: OB.font, color: OB.ink }}>
      <CrmToaster />
      <div style={{ flex: 'none', padding: '16px 32px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── header (Company.dc) ── */}
          <header style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
              <button onClick={() => navigate('/crm/onboardings')} aria-label="Back to all onboardings" style={{ width: 40, height: 40, border: `1px solid ${OB.border}`, borderRadius: 10, background: OB.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', color: OB.ink, fontWeight: 800, cursor: 'pointer', font: 'inherit' }}>←</button>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: OB.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flex: '0 0 48px' }}>{ob.companyName.slice(0, 2).toUpperCase()}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{ob.companyName}</h1>
                  <Pill bg={OB.accentSoft} fg={OB.accent} size={12} style={{ padding: '3px 10px' }}>Onboarding</Pill>
                  {ob.risk?.status && <Pill bg={ob.risk.status === 'Watch' ? OB.amberBg : OB.redBg} fg={ob.risk.status === 'Watch' ? OB.amberDark : OB.red} size={12} style={{ padding: '3px 10px' }}>{ob.risk.status}</Pill>}
                </div>
                <div style={{ fontSize: 13, color: OB.muted, minWidth: 0 }}>
                  {[meta.contact?.name, meta.contact?.email, ob.plan, deal?.mrr ? `£${Number(deal.mrr).toLocaleString('en-GB')}/mo` : null, `Account manager: ${ownerName}`].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <BtnPrimary onClick={() => setBookOpen(true)} style={{ minHeight: 44, fontWeight: 700 }}>Book meeting</BtnPrimary>
              <div style={{ position: 'relative' }}>
                <button aria-label="More actions" onClick={() => setMenuOpen(!menuOpen)} style={{ font: 'inherit', fontWeight: 800, fontSize: 16, background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 10, width: 44, height: 44, cursor: 'pointer' }}>···</button>
                {menuOpen && (
                  <div style={{ position: 'absolute', right: 0, zIndex: 30, marginTop: 4, width: 210, borderRadius: 10, border: `1px solid ${OB.border}`, background: OB.surface, boxShadow: '0 8px 24px rgba(27,36,51,.14)', padding: 6, display: 'flex', flexDirection: 'column' }} onMouseLeave={() => setMenuOpen(false)}>
                    {leadId && <MenuItem onClick={() => navigate(`/crm/lead/${leadId}`)}>Open lead screen</MenuItem>}
                    <MenuItem onClick={() => navigate(`/crm/onboardings/${ob.id}`)}>Classic onboarding page</MenuItem>
                    <MenuItem onClick={() => customerLink(false)}>Copy customer link</MenuItem>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* ── tab strip ── */}
          <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${OB.border}`, flexWrap: 'wrap' }}>
            {TABS.map((t) => <UTab key={t.key} on={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</UTab>)}
          </div>

      </div>

      {/* ── plan strip (CoPathways) — every tab except Overview — fixed ── */}
      <div style={{ flex: 'none', padding: '14px 32px 0' }}>
          {tab !== 'overview' && (
            <Card row pad="12px 16px" style={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Pill bg={OB.track} fg={OB.ink} size={12.5} style={{ padding: '3px 10px' }}>{ob.plan || 'No plan'}</Pill>
                <span style={{ fontSize: 13.5, fontWeight: 800, color: over > 0 ? OB.red : OB.ink }}>
                  {ob.hoursAllowance ? `${Math.min(ob.hoursUsed, ob.hoursAllowance)}/${ob.hoursAllowance} hours${over > 0 ? ` + ${over.toFixed(over % 1 ? 1 : 0)} over · chargeable` : ''}` : `${ob.hoursUsed}h used`}
                </span>
                {ob.hoursAllowance ? (
                  <span style={{ display: 'block', width: 160, height: 7, borderRadius: 4, background: OB.track, overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${Math.min(100, (ob.hoursUsed / ob.hoursAllowance) * 100)}%`, background: over > 0 ? OB.red : OB.accent }} />
                  </span>
                ) : null}
                {ob.paymentStatus === 'paid' && <Pill bg={OB.greenBg} fg={OB.green} size={12}>Paid</Pill>}
                {ob.paymentStatus === 'pending' && <Pill bg={OB.redBg} fg={OB.red} size={12}>Awaiting payment</Pill>}
                <span style={{ fontSize: 12.5, color: OB.muted }}>Go-live: {ob.goLiveDate ? fmtDay(ob.goLiveDate) : 'not set'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <BtnOutline onClick={() => customerLink(false)}>Customer link</BtnOutline>
                <BtnOutline onClick={topUp}>+ Top up hours</BtnOutline>
                <BtnSoft onClick={() => customerLink(true)}>Customer view →</BtnSoft>
                <BtnSoft onClick={() => ensureDraft('plan_hours', `${ob.hoursUsed}h of ${ob.hoursAllowance || '—'}h used`)}>Notify the customer</BtnSoft>
              </div>
            </Card>
          )}

      </div>

      {/* ── tab body — the lists scroll, the chrome above stays (Lee, 30 Oct) ── */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '14px 32px 16px' }}>
          {tab === 'overview' && <div style={SCROLL}><OverviewTab ob={ob} deal={deal} owner={ownerName} onChanged={load} ensureDraft={ensureDraft} setTab={setTab} /></div>}
          {tab === 'todo' && <TodoTab ob={ob} deal={deal} onChanged={load} />}
          {tab === 'pathways' && <div style={SCROLL}><ObPathwaysTab ob={ob} deal={deal} owner={ownerName} /></div>}
          {tab === 'sessions' && <SessionsTab ob={ob} deal={deal} owner={ownerName} onChanged={load} ensureDraft={ensureDraft} setTab={setTab} />}
          {tab === 'notes' && <NotesTab ob={ob} deal={deal} onChanged={load} />}
          {tab === 'platform' && <div style={SCROLL}><div style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px' }}>{deal ? <DealPlatform deal={deal} /> : <p style={{ fontSize: 12.5, color: OB.muted }}>No linked lead — platform data unavailable.</p>}</div></div>}
          {tab === 'comms' && <CommsTab ob={ob} deal={deal} ensureDraft={ensureDraft} />}
          {tab === 'team' && <div style={SCROLL}><div style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px' }}>{leadId ? <DealUsers dealId={leadId} dealName={ob.companyName} /> : <p style={{ fontSize: 12.5, color: OB.muted }}>No linked lead.</p>}</div></div>}
          {tab === 'workflow' && <div style={SCROLL}><WorkflowTab /></div>}
          {tab === 'plan' && <div style={SCROLL}><PlanTab ob={ob} deal={deal} ensureDraft={ensureDraft} navigate={navigate} setTab={setTab} /></div>}
          {tab === 'details' && <div style={SCROLL}><DetailsTab ob={ob} deal={deal} onChanged={load} navigate={navigate} /></div>}
      </div>

      {bookOpen && leadId && <BookingModal dealId={leadId} dealName={ob.companyName} initialType="meeting" onClose={() => setBookOpen(false)} onSaved={() => { setBookOpen(false); load() }} />}
    </div>
  )
}

const MenuItem: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ font: 'inherit', fontSize: 13, textAlign: 'left', background: 'none', border: 0, borderRadius: 6, padding: '7px 8px', cursor: 'pointer', color: OB.ink }}
    onMouseEnter={(e) => ((e.target as HTMLElement).style.background = OB.headerBg)} onMouseLeave={(e) => ((e.target as HTMLElement).style.background = 'none')}>{children}</button>
)

/* ══ Overview — Company.dc.html ══ */
const OverviewTab: React.FC<{ ob: any; deal: any; owner: string; onChanged: () => void; ensureDraft: (k: string, w: string) => void; setTab: (t: Tab) => void }> = ({ ob, deal, owner, onChanged, ensureDraft, setTab }) => {
  const navigate = useNavigate()
  const [todos, setTodos] = useState<TodoRow[]>([])
  const [files, setFiles] = useState<any[]>([])
  const [fileState, setFileState] = useState<Record<string, 'add' | 'done' | 'wrong'>>({})
  const [drafts, setDrafts] = useState<any[]>([])
  useEffect(() => {
    apiGet<{ rows: TodoRow[] }>('/crm/onboarding/todos').then((r) => setTodos(r.rows.filter((x) => x.company === ob.companyName))).catch(() => {})
    obFiles(ob.id).then(setFiles).catch(() => {})
    if (ob.leadId) apiGet<{ drafts: any[] }>(`/crm/ob-drafts?leadId=${ob.leadId}&status=to_send`).then((r: any) => setDrafts(r.drafts || r || [])).catch(() => {})
  }, [ob.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const ours = todos.filter((t) => t.side === 'ours' && t.taskId)
  const theirs = todos.filter((t) => t.side === 'theirs' && t.taskId)
  const over = ob.hoursAllowance ? Math.max(0, ob.hoursUsed - ob.hoursAllowance) : 0
  const watching = ob.risk?.status === 'Watch'
  const waitingDraft = drafts.find((d) => d.type === 'Follow-up') || drafts[0]

  /* summary built from the live numbers — no invention */
  const summaryBits: string[] = []
  const curStage = (ob.stages || [])[ob.currentStage]
  if (curStage) summaryBits.push(`${curStage.name || curStage.short}: ${curStage.done} of ${curStage.total} checkpoints done.`)
  if (over > 0) summaryBits.push(`${over.toFixed(over % 1 ? 1 : 0)} hours over the ${ob.plan || ''} plan.`.replace('  ', ' '))
  const overdueOurs = ours.filter((t) => (t.overdueDays || 0) > 0).length
  const overdueTheirs = theirs.filter((t) => (t.overdueDays || 0) > 0).length
  if (overdueOurs + overdueTheirs > 0) summaryBits.push(`${overdueOurs + overdueTheirs} overdue: ${overdueOurs} ours, ${overdueTheirs} theirs.`)
  if (!ob.nextMeeting) summaryBits.push('No next session booked.')

  /* pathway mini-cards from ob.stages */
  const pathCards = useMemo(() => {
    const groups = new Map<string, any[]>()
    ;(ob.stages || []).forEach((s: any, i: number) => {
      if (s.hidden) return
      const p = pathwayOfShort(s.short || s.name || '')
      if (!groups.has(p)) groups.set(p, [])
      groups.get(p)!.push({ ...s, idx: i })
    })
    return ['Training & Setup', 'Data migration', 'Proposal set-up'].filter((n) => groups.has(n)).map((n) => {
      const list = groups.get(n)!
      const cur = list.find((s) => s.idx === ob.currentStage) || list.find((s) => !s.complete && !s.locked)
      return { name: n, list, cur }
    })
  }, [ob])

  const dotStyle = (s: any): React.CSSProperties =>
    s.complete ? { width: 11, height: 11, borderRadius: '50%', background: '#6B7686' }
    : s.idx === ob.currentStage ? { width: 13, height: 13, borderRadius: '50%', background: OB.accentSoft, border: `2.5px solid ${OB.accent}`, boxSizing: 'border-box' }
    : { width: 11, height: 11, borderRadius: '50%', background: '#fff', border: `1.5px solid ${OB.faint2}`, boxSizing: 'border-box' }

  const red: React.CSSProperties = { fontSize: 12, fontWeight: 800, color: OB.red, whiteSpace: 'nowrap' }
  const grey: React.CSSProperties = { fontSize: 12, color: OB.muted, whiteSpace: 'nowrap' }

  const customerFiles = files.filter((f: any) => f.mine === false || f.uploadedBy === 'customer').slice(0, 12)

  return (
    <>
      {/* payment banner */}
      {ob.paymentStatus === 'pending' && (
        <section style={{ background: OB.redPale, border: `1px solid ${OB.redBorder}`, borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <Pill bg={OB.redBg} fg={OB.red} size={12} style={{ padding: '3px 10px' }}>Awaiting payment</Pill>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 420px' }}>
            <strong style={{ fontSize: 14 }}>Onboarding payment{ob.price ? ` of £${Number(ob.price).toLocaleString('en-GB')}` : ''} not received</strong>
            <span style={{ fontSize: 13, color: OB.ink2 }}>Onboarding hasn't started: their onboarding page opens and the welcome email goes once it's paid.</span>
          </span>
          <button onClick={() => ensureDraft('payment_reminder', 'Onboarding payment outstanding')} style={{ font: 'inherit', fontSize: 13, fontWeight: 700, background: OB.surface, color: OB.ink, border: `1px solid ${OB.inputBorder}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer' }}>Open payment reminder</button>
          <BtnPrimary small onClick={() => { if (window.confirm('Mark the onboarding payment as paid?')) obMarkPaid(ob.id).then(() => { crmToast('Marked paid', 'success'); onChanged() }) }}>Mark as paid</BtnPrimary>
        </section>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* status row */}
          <Card row pad="12px 16px" style={{ borderRadius: 14, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: OB.muted, letterSpacing: '.05em' }}>STATUS</span>
              {ob.risk?.status
                ? <Pill bg={ob.risk.status === 'Watch' ? OB.amberBg : OB.redBg} fg={ob.risk.status === 'Watch' ? OB.amberDark : OB.red} size={13} style={{ padding: '4px 12px' }}>{ob.risk.status}</Pill>
                : <Pill bg={OB.greenBg} fg={OB.green} size={13} style={{ padding: '4px 12px' }}>On track</Pill>}
              <span style={{ fontSize: 13, color: OB.muted }}>{(ob.risk?.reasons || []).join(' · ') || (over > 0 ? `${over.toFixed(over % 1 ? 1 : 0)}h over plan` : 'No risk signals')}</span>
            </div>
            <button
              onClick={() => obSetRisk(ob.id, watching ? { status: null } : ({ status: 'Watch', reasons: [], ownerId: ob.ownerId || undefined } as any)).then(() => { crmToast(watching ? 'Stopped watching' : 'Watching', 'success'); onChanged() }).catch(() => crmToast('Could not update', 'error'))}
              style={{ font: 'inherit', fontSize: 13, fontWeight: 800, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', ...(watching ? { background: '#E4EDFC', color: OB.accent, border: `1.5px solid ${OB.accent}` } : { background: OB.accent, color: '#fff', border: 0 }) }}>
              {watching ? 'Watching · stop' : 'Start watching'}
            </button>
          </Card>

          {/* summary + follow-up waiting */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, alignItems: 'stretch' }}>
            <section style={{ background: OB.accentPale, border: `1px solid ${OB.accentBorder}`, borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <strong style={{ fontSize: 15 }}>Where this onboarding is</strong>
              <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{summaryBits.join(' ') || 'Nothing flagged.'}</div>
              <span style={{ fontSize: 11.5, color: OB.muted }}>From the pathways, sessions, plan hours and to-dos — live</span>
            </section>
            {waitingDraft && (
              <section style={{ background: OB.amberPale, border: `1.5px solid ${OB.amberBorder}`, borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <strong style={{ fontSize: 15, color: OB.amberInk }}>{waitingDraft.type === 'Follow-up' ? 'Follow-up waiting for sign-off' : `${waitingDraft.type} draft waiting`}</strong>
                  <span style={{ fontSize: 13, color: OB.amberInk }}>{waitingDraft.subject || waitingDraft.why || ''}</span>
                </div>
                <button onClick={() => navigate(`/crm/onboardings/followups?draft=${waitingDraft.id}`)} style={{ font: 'inherit', fontSize: 14, fontWeight: 800, background: OB.accent, color: '#fff', border: 0, borderRadius: 10, padding: '9px 14px', alignSelf: 'flex-start', cursor: 'pointer' }}>Review and send</button>
              </section>
            )}
          </div>

          {/* meetings strip */}
          <section aria-label="Meetings and contact" style={{ background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Eyebrow>Next meeting</Eyebrow>
              {ob.nextMeeting ? (
                <span style={{ fontSize: 15.5, color: OB.ink }}>{new Date(ob.nextMeeting.startAt).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              ) : (
                <>
                  <span style={{ fontSize: 15.5, color: OB.faint2 }}>None booked</span>
                  <span style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                    <BtnSoft style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={() => window.open('https://calendly.com/wequote/onboarding-training-call', '_blank', 'noopener')}>Book</BtnSoft>
                    <BtnSoft style={{ padding: '4px 10px', fontSize: 12.5 }} onClick={() => ensureDraft('booking_link', 'No next session booked')}>Send booking link</BtnSoft>
                  </span>
                </>
              )}
            </div>
            <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: `1px solid ${OB.border}` }}>
              <Eyebrow>Last email</Eyebrow>
              {ob.lastEmail?.at ? (
                <>
                  <span style={{ fontSize: 15.5, color: OB.ink }}>{fmtDay(ob.lastEmail.at)} · {ob.lastEmail.direction === 'in' ? 'them → us' : 'us → them'}</span>
                  {ob.lastEmail.direction === 'in' && <span style={{ fontSize: 12.5, fontWeight: 700, color: OB.amber }}>We haven't replied</span>}
                </>
              ) : <span style={{ fontSize: 15.5, color: OB.faint2 }}>None synced</span>}
            </div>
            <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 4, borderLeft: `1px solid ${OB.border}` }}>
              <Eyebrow>Last meeting</Eyebrow>
              {(ob.lastHeldAt || ob.lastMeeting?.at) ? (
                <>
                  <span style={{ fontSize: 15.5, color: OB.ink }}>{fmtDay(ob.lastHeldAt || ob.lastMeeting?.at)}{ob.lastMeeting?.by ? ` · ${ob.lastMeeting.by}` : ''}</span>
                  <button onClick={() => setTab('sessions')} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>{ob.lastMeeting?.title || 'Recap and transcript'} →</button>
                </>
              ) : <span style={{ fontSize: 15.5, color: OB.faint2 }}>None held</span>}
            </div>
          </section>

          {/* pathways so far */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 15 }}>Pathways so far</strong>
              <button onClick={() => setTab('pathways')} style={{ font: 'inherit', fontSize: 13, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>Every station and checkpoint is on the Pathways tab →</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              {pathCards.map((p) => (
                <div key={p.name} style={{ background: OB.surface, borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, border: `1px solid ${OB.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 15 }}>{p.name}</strong>
                    <span style={{ fontSize: 12, color: OB.muted }}>{owner}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {p.list.map((s: any) => <span key={s.idx} title={s.short || s.name} style={dotStyle(s)} />)}
                  </div>
                  <div style={{ fontSize: 13 }}>{p.cur ? `${p.cur.short || p.cur.name} · ${p.cur.done} of ${p.cur.total}` : 'All stations done'}</div>
                  <div style={{ fontSize: 12, color: OB.muted }}>{p.list.filter((s: any) => s.complete).length} of {p.list.length} stops done</div>
                </div>
              ))}
            </div>
          </section>

          {/* two to-do columns */}
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {([['WeQuote to do', ours, false], ['Customer to do', theirs, true]] as Array<[string, TodoRow[], boolean]>).map(([title, rows, chase]) => (
              <div key={title} style={{ background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 15 }}>{title}</strong>
                  <span style={{ fontSize: 12, color: OB.muted }}>{rows.length} open · {rows.filter((r) => (r.overdueDays || 0) > 0).length} overdue</span>
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {rows.map((r) => (
                    <div key={r.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: `1px solid ${OB.rowDiv}` }}>
                      <button title="Complete" onClick={() => r.taskId && updateTask(r.taskId, { done: true }).then(() => { crmToast('Done ✓', 'success'); onChanged() })}
                        style={{ width: 16, height: 16, border: `1.5px solid ${OB.faint2}`, background: '#fff', borderRadius: 4, boxSizing: 'border-box', marginTop: 2, flex: 'none', cursor: 'pointer' }} />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 13.5 }}><strong>{r.ownerName || (chase ? 'Customer' : 'No owner')}:</strong> {r.title}</span>
                        {r.origin && <span style={{ fontSize: 12, color: OB.muted }}>{r.origin}</span>}
                      </div>
                      <span style={(r.overdueDays || 0) > 0 ? red : grey}>{(r.overdueDays || 0) > 0 ? `Overdue ${r.overdueDays}d` : r.dueAt ? `Due ${fmtDay(r.dueAt)}` : ''}</span>
                      {chase && r.taskId && <BtnSoft style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => apiPost(`/crm/tasks/${r.taskId}/chase`, {}).then(() => { crmToast('Chase drafted', 'success'); onChanged() })}>Chase</BtnSoft>}
                    </div>
                  ))}
                  {!rows.length && <span style={{ fontSize: 12.5, color: OB.faint, padding: '8px 0' }}>Nothing open</span>}
                </div>
              </div>
            ))}
          </section>
        </div>

        {/* ── aside ── */}
        <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card pad="14px 16px" gap={6}>
            <strong style={{ fontSize: 14 }}>Recent messages</strong>
            {ob.lastEmail?.at && (
              <div style={{ fontSize: 13, borderTop: `1px solid ${OB.rowDiv}`, paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span><strong>{ob.lastEmail.direction === 'in' ? ob.lastEmail.from : 'Us'}</strong> · Email · {fmtDay(ob.lastEmail.at)}</span>
                <span style={{ color: OB.muted }}>{ob.lastEmail.direction === 'in' ? `to ${ob.lastEmail.to || 'us'}` : `to ${ob.lastEmail.to || 'them'}`} · via {ob.lastEmail.via}</span>
              </div>
            )}
            {!ob.lastEmail?.at && <span style={{ fontSize: 12.5, color: OB.faint }}>Nothing synced yet</span>}
            <button onClick={() => setTab('comms')} style={{ font: 'inherit', fontSize: 13, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>All emails and chats</button>
          </Card>
          <Card pad="14px 16px" gap={6}>
            <strong style={{ fontSize: 14 }}>Platform activity</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, fontSize: 13 }}>
              <div><div style={{ fontSize: 20, fontWeight: 800 }}>{ob.lastLoginAt ? fmtDay(ob.lastLoginAt) : '—'}</div><div style={{ color: OB.muted }}>last login</div></div>
              <div><div style={{ fontSize: 20, fontWeight: 800 }}>{ob.lastQuoteAt ? fmtDay(ob.lastQuoteAt) : '—'}</div><div style={{ color: OB.muted }}>last quote</div></div>
            </div>
            {ob.lastQuoteAcceptedAt && <span style={{ fontSize: 13 }}>Last quote accepted: {fmtDay(ob.lastQuoteAcceptedAt)}</span>}
            <button onClick={() => setTab('platform')} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>Platform tab →</button>
          </Card>
          <Card pad="14px 16px" gap={8}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <strong style={{ fontSize: 14 }}>Files received</strong>
              <span style={{ fontSize: 12, color: OB.muted }}>Does it need work from us?</span>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
              {customerFiles.map((f: any) => {
                const st = fileState[f.id]
                return (
                  <div key={f.id} style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 5, borderTop: `1px solid ${OB.rowDiv}`, paddingTop: 8 }}>
                    <span><a href={f.url} target="_blank" rel="noreferrer" style={{ fontWeight: 800, textDecoration: 'none' }}>{f.name}</a>{f.createdAt ? ` · ${fmtDay(f.createdAt)}` : ''}</span>
                    {!st && (
                      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => { if (ob.leadId) addTask(ob.leadId, { title: `Check "${f.name}"`, side: 'ours', source: 'file', sourceRef: f.id } as any).then(() => { setFileState({ ...fileState, [f.id]: 'add' }); crmToast('Added to our to-do', 'success'); onChanged() }) }}
                          style={{ font: 'inherit', fontSize: 12, fontWeight: 700, background: OB.accent, color: '#fff', border: 0, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>Add to our to-do</button>
                        <button onClick={() => setFileState({ ...fileState, [f.id]: 'done' })}
                          style={{ font: 'inherit', fontSize: 12, fontWeight: 700, background: OB.greenBg, color: OB.green, border: 0, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>Nothing to do · tick off</button>
                      </span>
                    )}
                    {st === 'add' && <span style={{ fontSize: 12.5, fontWeight: 700, color: OB.green }}>✓ Added to our to-do</span>}
                    {st === 'done' && <span style={{ fontSize: 12.5, fontWeight: 700, color: OB.green }}>✓ Nothing for us to do</span>}
                  </div>
                )
              })}
              {!customerFiles.length && <span style={{ fontSize: 12.5, color: OB.faint }}>None yet</span>}
            </div>
          </Card>
          <Card pad="14px 16px" gap={6}>
            <strong style={{ fontSize: 14 }}>Plan</strong>
            <span style={{ fontSize: 13 }}>
              {ob.plan || 'No plan'} {ob.hoursAllowance ? `${Math.min(ob.hoursUsed, ob.hoursAllowance)}/${ob.hoursAllowance}h` : `${ob.hoursUsed}h`}
              {over > 0 && <strong style={{ color: OB.red }}> +{over.toFixed(over % 1 ? 1 : 0)}h chargeable</strong>}
            </span>
            {ob.hoursAllowance ? (
              <div style={{ height: 8, background: over > 0 ? '#F3DDB2' : OB.track, borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: over > 0 ? `${(ob.hoursAllowance / ob.hoursUsed) * 100}%` : `${Math.min(100, (ob.hoursUsed / ob.hoursAllowance) * 100)}%`, background: over > 0 ? OB.amberDot : OB.accent }} />
                {over > 0 && <div style={{ flex: 1, background: OB.red }} />}
              </div>
            ) : null}
            <button onClick={() => ensureDraft('plan_hours', `${ob.hoursUsed}h of ${ob.hoursAllowance || '—'}h used`)} style={{ font: 'inherit', fontSize: 13, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>Notify the customer</button>
          </Card>
        </aside>
      </div>
    </>
  )
}

/* ══ To-do tab — CoTodo.dc.html ══ */
const TodoTab: React.FC<{ ob: any; deal: any; onChanged: () => void }> = ({ ob, deal, onChanged }) => {
  const [filter, setFilter] = useState('All')
  const tasks: any[] = (deal?.tasks || []).filter((t: any) => !t.isDefault && !t.done)
  const presale: any[] = (deal?.tasks || []).filter((t: any) => t.isDefault)
  const [showPresale, setShowPresale] = useState(false)
  const owners = [...new Set(tasks.filter((t) => (t.side || 'ours') === 'ours' && t.ownerName).map((t) => t.ownerName as string))]
  const chips = ['All', 'Overdue', ...owners, 'Unassigned']
  const match = (t: any) => {
    if (filter === 'All') return true
    if (filter === 'Overdue') return t.due != null && t.due < 0 && t.ownerName
    if (filter === 'Unassigned') return (t.side || 'ours') === 'ours' && !t.ownerName
    return t.ownerName === filter
  }
  const addNew = () => {
    const title = window.prompt('New task — what needs doing?')
    if (!title?.trim() || !ob.leadId) return
    addTask(ob.leadId, { title: title.trim() } as any).then(() => { crmToast('Task added', 'success'); onChanged() })
  }
  const extend = (t: any) => {
    const d = new Date(Date.now() + 7 * 864e5)
    updateTask(t.id, { dueAt: d.toISOString() }).then(() => { crmToast('Extended a week', 'success'); onChanged() }).catch(() => crmToast('Could not extend', 'error'))
  }
  const assign = (t: any) => {
    const who = window.prompt('Assign to (name)?')
    if (!who?.trim()) return
    updateTask(t.id, { ownerName: who.trim() }).then(() => { crmToast('Assigned', 'success'); onChanged() }).catch(() => crmToast('Could not assign', 'error'))
  }
  const groupOf = (t: any) => ((t.side || 'ours') === 'ours' && !t.ownerName) ? 'Unassigned' : [t.pathway, t.station].filter(Boolean).join(' · ') || 'General'
  const TaskCardList: React.FC<{ title: string; side: 'ours' | 'theirs' }> = ({ title, side }) => {
    const list = tasks.filter((t) => (t.side || 'ours') === side)
    const shown = list.filter(match)
    const groups = new Map<string, any[]>()
    for (const t of shown) { const g = groupOf(t); if (!groups.has(g)) groups.set(g, []); groups.get(g)!.push(t) }
    const ordered = [...groups.entries()].sort((a, b) => (a[0] === 'Unassigned' ? 1 : 0) - (b[0] === 'Unassigned' ? 1 : 0))
    return (
      <Card style={{ height: '100%', boxSizing: 'border-box', minHeight: 0 }}>
        <div style={{ flex: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <span style={{ fontSize: 12.5, color: OB.muted }}>{list.length} open · {list.filter((t) => t.due != null && t.due < 0).length} overdue</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {ordered.map(([g, rows]) => (
            <React.Fragment key={g}>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: OB.faint, padding: '10px 0 2px' }}>{g}</div>
              {rows.map((t) => {
                const unassigned = g === 'Unassigned'
                const overdue = t.due != null && t.due < 0
                return (
                  <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderTop: `1px solid ${OB.rowDiv}` }}>
                    <button aria-label="Complete" onClick={() => updateTask(t.id, { done: true }).then(() => { crmToast('Done ✓', 'success'); onChanged() })}
                      style={{ width: 18, height: 18, borderRadius: 4, marginTop: 2, fontSize: 11, fontWeight: 800, cursor: 'pointer', background: '#fff', border: `1.5px solid ${OB.faint2}`, boxSizing: 'border-box', flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 13.5, color: unassigned ? OB.red : OB.ink, overflowWrap: 'anywhere' }}><strong>{t.ownerName || (side === 'theirs' ? 'Customer' : 'No owner')}:</strong> {t.title}</span>
                      <span style={{ fontSize: 12, color: OB.muted }}>{t.source && t.source !== 'manual' ? `From ${t.source}` : 'Added manually'}{t.lastChasedAt ? ` · chased ${new Date(t.lastChasedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flex: 'none' }}>
                      <span style={{ fontSize: 12, fontWeight: overdue || unassigned ? 800 : 400, color: overdue || unassigned ? OB.red : OB.muted, whiteSpace: 'nowrap' }}>
                        {overdue ? `Overdue ${-t.due} day${t.due === -1 ? '' : 's'}` : t.due != null ? (t.due === 0 ? 'Due today' : `Due in ${t.due}d`) : ''}
                      </span>
                      <span style={{ display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
                        {unassigned && <MiniBtn onClick={() => assign(t)}>Assign</MiniBtn>}
                        {side === 'theirs' && t.id && <MiniBtn onClick={() => apiPost(`/crm/tasks/${t.id}/chase`, {}).then(() => { crmToast('Chase drafted', 'success'); onChanged() })}>Chase</MiniBtn>}
                        <MiniBtn onClick={() => extend(t)}>Extend</MiniBtn>
                      </span>
                    </div>
                  </div>
                )
              })}
            </React.Fragment>
          ))}
          {!shown.length && <span style={{ fontSize: 12.5, color: OB.faint, display: 'block', padding: '10px 0' }}>Nothing here</span>}
        </div>
      </Card>
    )
  }
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
      <div style={{ flex: 'none', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {chips.map((c) => <Chip key={c} on={filter === c} onClick={() => setFilter(c)}>{c}</Chip>)}
        </div>
        <BtnPrimary small onClick={addNew}>+ Add task</BtnPrimary>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12, alignItems: 'stretch', overflow: 'hidden' }}>
        <TaskCardList title="WeQuote to do" side="ours" />
        <TaskCardList title="Customer to do" side="theirs" />
      </div>
      {presale.length > 0 && (
        <Card row style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}><strong>Pre-sale checklist</strong> <span style={{ color: OB.muted }}>(the live Tasks list: demo booked, demo completed, trial started…) · {presale.filter((t) => t.done).length} of {presale.length} done before onboarding</span></span>
          <BtnOutline onClick={() => setShowPresale(!showPresale)}>{showPresale ? 'Hide' : 'Show'}</BtnOutline>
        </Card>
      )}
      {showPresale && (
        <Card>
          {presale.map((t) => <span key={t.id} style={{ fontSize: 13, padding: '3px 0', color: t.done ? OB.faint : OB.ink, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>)}
        </Card>
      )}
    </div>
  )
}
const MiniBtn: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ font: 'inherit', fontSize: 11.5, fontWeight: 700, background: OB.track, color: OB.ink, border: 0, borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>{children}</button>
)

/* ══ Sessions — CoSessions.dc.html ══ */
const SessionsTab: React.FC<{ ob: any; deal: any; owner: string; onChanged: () => void; ensureDraft: (k: string, w: string) => void; setTab: (t: Tab) => void }> = ({ ob, deal, owner, onChanged, ensureDraft, setTab }) => {
  const meetings: any[] = ob.meetings || []
  const held = meetings.filter((m) => m.status === 'held').sort((a, b) => new Date(a.startAt || 0).getTime() - new Date(b.startAt || 0).getTime())
  const planned = meetings.filter((m) => m.status === 'planned' && m.startAt && new Date(m.startAt) > new Date()).sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
  const [showAll, setShowAll] = useState(false)
  const [openId, setOpenId] = useState<string | null>(held.length ? held[held.length - 1].id : null)
  const timeline = showAll ? held : held.slice(-6)
  const over = ob.hoursAllowance ? Math.max(0, ob.hoursUsed - ob.hoursAllowance) : 0
  const last30 = held.filter((m) => m.startAt && Date.now() - new Date(m.startAt).getTime() < 30 * 864e5).length
  const openStations = (ob.stages || []).map((s: any, i: number) => ({ ...s, idx: i })).filter((s: any) => !s.hidden && !s.complete)
  const unlocked = openStations.filter((s: any) => !s.locked)
  const cur = (ob.stages || [])[ob.currentStage]
  const curSteps: any[] = (ob.steps || []).filter((s: any) => s.stageIndex === ob.currentStage)
  const nextStep = curSteps.find((s: any) => !s.done)
  const stageColor = (i: number) => {
    const short = ((ob.stages || [])[i] || {}).short || ''
    return /crm/i.test(short) ? PATHWAY_COLORS['Data migration'] : /bespoke/i.test(short) ? PATHWAY_COLORS['Proposal set-up'] : PATHWAY_COLORS['Training & Setup']
  }
  const tasks: any[] = (deal?.tasks || []).filter((t: any) => !t.isDefault && !t.done)
  const holdups: Array<{ sev: 0 | 1 | 2; title: string; src: string; btn: string; act: () => void }> = []
  for (const t of tasks.filter((t) => (t.side || 'ours') === 'theirs' && t.due != null && t.due < 0)) {
    holdups.push({ sev: 2, title: t.title, src: `${t.ownerName || 'Customer'} · overdue ${-t.due}d${t.lastChasedAt ? ` · chased ${new Date(t.lastChasedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}`, btn: 'Chase', act: () => apiPost(`/crm/tasks/${t.id}/chase`, {}).then(() => { crmToast('Chase drafted', 'success'); onChanged() }) })
  }
  for (const t of tasks.filter((t) => (t.side || 'ours') === 'ours' && t.due != null && t.due < 0)) {
    holdups.push({ sev: 1, title: t.title, src: `${t.ownerName || 'No owner'} · overdue ${-t.due}d`, btn: 'To-do', act: () => setTab('todo') })
  }
  if (ob.lastEmail?.at && ob.lastEmail.direction === 'in') holdups.push({ sev: 1, title: `We haven't replied to ${ob.lastEmail.from}`, src: `Email · ${fmtDay(ob.lastEmail.at)}`, btn: 'Reply', act: () => setTab('comms') })
  if (over > 0 && !ob.overNotice) holdups.push({ sev: 0, title: `${over.toFixed(over % 1 ? 1 : 0)}h over plan, customer not told`, src: 'Plan & payments', btn: 'Notify', act: () => ensureDraft('plan_hours', `${ob.hoursUsed}h of ${ob.hoursAllowance}h used`) })
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16, overflow: 'hidden' }}>
      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Card gap={4}><Eyebrow>Sessions held</Eyebrow><span style={{ fontSize: 20, fontWeight: 800 }}>{held.length}</span><span style={{ fontSize: 12.5, color: OB.muted }}>{held.length ? `Since ${fmtDay(held[0].startAt)} · ${last30} in the last 30 days` : 'None yet'}</span></Card>
        <Card gap={4}><Eyebrow>Still to do</Eyebrow><span style={{ fontSize: 20, fontWeight: 800, color: OB.accent }}>~{openStations.length}</span><span style={{ fontSize: 12.5, color: OB.muted }}>Stations left on the plan</span></Card>
        <Card gap={4}><Eyebrow>Plan hours</Eyebrow><span style={{ fontSize: 20, fontWeight: 800, color: over > 0 ? OB.red : OB.ink }}>{ob.hoursUsed}h / {ob.hoursAllowance || '—'}h</span><span style={{ fontSize: 12.5, color: OB.muted }}>{over > 0 ? `${over.toFixed(over % 1 ? 1 : 0)}h over ${ob.plan || 'plan'} · chargeable` : 'Within plan'}</span></Card>
        <Card gap={4}><Eyebrow>Training weeks</Eyebrow><span style={{ fontSize: 20, fontWeight: 800 }}>{held.length ? Math.max(1, Math.round((Date.now() - new Date(held[0].startAt).getTime()) / (7 * 864e5))) : 0}</span><span style={{ fontSize: 12.5, color: OB.muted }}>Since the first session</span></Card>
      </div>
      {/* next session + hold-ups */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, alignItems: 'stretch' }}>
        <Card style={{ border: `1.5px solid ${OB.accentBorder}`, background: '#F5F8FE' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Eyebrow>Next session</Eyebrow>
            {planned.length
              ? <span style={{ fontSize: 15.5 }}>{planned[0].subject || 'Session'} · {fmtDayTime(planned[0].startAt)}</span>
              : <span style={{ fontSize: 15.5, color: OB.faint2 }}>None booked</span>}
          </div>
          {cur && <span style={{ fontSize: 13.5 }}><strong>Recommended:</strong> {cur.short || cur.name}{nextStep != null ? ` — next checkpoint ${nextStep.stepIndex + 1}` : ''}</span>}
          <span style={{ fontSize: 12.5, color: OB.muted }}>
            {held.length ? `Last session ${fmtDay(held[held.length - 1].startAt)} (${held[held.length - 1].subject || 'session'}).` : 'No sessions held yet.'}
            {!planned.length && ' No session booked this week.'}
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnPrimary small onClick={() => window.open('https://calendly.com/wequote/onboarding-training-call', '_blank', 'noopener')}>Book a session</BtnPrimary>
            <BtnSoft onClick={() => ensureDraft('booking_link', 'No next session booked')}>Send booking link</BtnSoft>
          </div>
        </Card>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 15 }}>Hold-ups and outstanding</strong>
            <button onClick={() => setTab('todo')} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>All on To-do →</button>
          </div>
          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
            {holdups.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: `1px solid ${OB.rowDiv}`, alignItems: 'flex-start' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', marginTop: 5, flex: 'none', background: h.sev === 2 ? OB.red : h.sev === 1 ? OB.amberDot : OB.faint2 }} />
                <span style={{ fontSize: 13, flex: 1, minWidth: 0 }}><strong>{h.title}</strong> <span style={{ color: OB.muted }}>· {h.src}</span></span>
                <button onClick={h.act} style={{ font: 'inherit', fontSize: 12, fontWeight: 700, background: OB.accentSoft, color: OB.accent, border: 0, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', flex: 'none' }}>{h.btn}</button>
              </div>
            ))}
            {!holdups.length && <span style={{ fontSize: 12.5, color: OB.faint, display: 'block', padding: '6px 0' }}>Nothing outstanding</span>}
          </div>
        </Card>
      </div>
      {/* sessions held timeline — scrolls */}
      <Card pad="18px 20px" gap={0} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', paddingBottom: 6 }}>
          <strong style={{ fontSize: 15 }}>Sessions held</strong>
          <span style={{ fontSize: 12.5, color: OB.muted }}>Oldest to newest · click a stop for the recap</span>
        </div>
        {held.length > 6 && !showAll && (
          <button onClick={() => setShowAll(true)} style={{ font: 'inherit', background: 'none', border: 0, color: OB.accent, fontSize: 12.5, fontWeight: 700, margin: '0 0 6px 114px', alignSelf: 'flex-start', cursor: 'pointer' }}>↑ Show {held.length - 6} earlier sessions{held[0]?.startAt ? ` (from ${fmtDay(held[0].startAt)})` : ''}</button>
        )}
        {timeline.map((m, i) => {
          const c = stageColor(m.stageIndex ?? ob.currentStage)
          const isOpen = openId === m.id
          const first = i === 0 && (showAll || held.length <= 6)
          const last = i === timeline.length - 1
          return (
            <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '70px 34px 1fr', columnGap: 10, alignItems: 'stretch' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: OB.muted, textAlign: 'right', paddingTop: 14 }}>{m.startAt ? new Date(m.startAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}</span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ width: 4, height: 16, background: first ? 'transparent' : c }} />
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', border: `4px solid ${c}`, boxSizing: 'border-box', flex: 'none' }} />
                <span style={{ width: 4, flex: 1, minHeight: 18, background: last ? 'transparent' : c }} />
              </span>
              <div style={{ padding: '6px 0 10px' }}>
                <button onClick={() => setOpenId(isOpen ? null : m.id)} style={{ font: 'inherit', width: '100%', background: OB.headerBg, border: `1px solid ${OB.border}`, borderRadius: 10, padding: '9px 12px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer' }}>
                  <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: OB.accent, fontWeight: 800 }}>{isOpen ? '▾' : '▸'}</span>
                    <span style={{ fontSize: 14, fontWeight: 800 }}>{m.subject || 'Session'}</span>
                    <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '2px 8px', background: OB.greenBg, color: OB.green }}>HELD</span>
                    {m.stageIndex != null && (ob.stages || [])[m.stageIndex] && (
                      <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '1px 7px', background: '#fff', border: `1.5px solid ${c}`, color: c }}>{((ob.stages || [])[m.stageIndex].short) || ''}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 12, color: OB.muted, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>{[m.bookedBy, (m.attendees || []).slice(0, 3).join(', '), m.durationMin ? `${m.durationMin} min` : null].filter(Boolean).join(' · ')}</span>
                  </span>
                </button>
                {isOpen && (
                  <div style={{ marginTop: 6, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 10, padding: '12px 14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 13 }}>
                    <div><Eyebrow>What we covered</Eyebrow><div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{m.summary || '—'}</div></div>
                    <div><Eyebrow>Blockers</Eyebrow><div style={{ marginTop: 4 }}>{m.blockers || '—'}</div></div>
                    <div><Eyebrow>Next</Eyebrow><div style={{ marginTop: 4 }}>{m.nextAct || '—'}</div></div>
                    <div>
                      <Eyebrow>Details</Eyebrow>
                      <div style={{ marginTop: 4, color: OB.muted }}>{(m.attendees || []).join(', ') || '—'}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {m.transcriptRef && <a href={m.transcriptRef} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 700, background: OB.accentSoft, color: OB.accent, borderRadius: 8, padding: '5px 10px', textDecoration: 'none' }}>Transcript</a>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {!held.length && <span style={{ fontSize: 12.5, color: OB.faint, padding: '8px 0' }}>No sessions held yet.</span>}
        {/* today marker */}
        <div style={{ display: 'grid', gridTemplateColumns: '70px 34px 1fr', columnGap: 10, alignItems: 'center', padding: '2px 0' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: OB.red, textAlign: 'right' }}>Today</span>
          <span style={{ display: 'flex', justifyContent: 'center' }}><span style={{ width: 26, height: 4, borderRadius: 2, background: OB.red }} /></span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: OB.red }}>{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{planned.length ? '' : ' · no session booked this week'}</span>
        </div>
        {/* planned + recommended next */}
        {planned.map((m) => (
          <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '70px 34px 1fr', columnGap: 10, alignItems: 'stretch' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: OB.muted, textAlign: 'right', paddingTop: 14 }}>{fmtDay(m.startAt)}</span>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', border: `4px solid ${OB.accent}`, boxSizing: 'border-box', marginTop: 10 }} />
            </span>
            <div style={{ background: OB.accentPale, border: `1.5px solid ${OB.accentMid}`, borderRadius: 10, padding: '9px 12px', margin: '6px 0 10px', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{m.subject || 'Session'} · {m.startAt ? new Date(m.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
              <button onClick={() => { if (window.confirm('Cancel this session?')) obUpdateMeeting(ob.id, m.id, { status: 'cancelled' }).then(() => { crmToast('Cancelled', 'success'); onChanged() }) }} style={{ font: 'inherit', fontSize: 12, fontWeight: 700, color: OB.red, background: 'none', border: 0, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ))}
        {unlocked.slice(0, 4).filter((s: any) => s.idx !== ob.currentStage).map((s: any, i: number, arr: any[]) => {
          const c = stageColor(s.idx)
          return (
            <div key={s.idx} style={{ display: 'grid', gridTemplateColumns: '70px 34px 1fr', columnGap: 10, alignItems: 'stretch' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: OB.muted, textAlign: 'right', paddingTop: 14 }}>Up next</span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', border: `3px dashed ${c}`, boxSizing: 'border-box', marginTop: 10 }} />
                <span style={{ width: 4, flex: 1, minHeight: 10, background: i === arr.length - 1 ? 'transparent' : 'repeating-linear-gradient(180deg,' + OB.lockBorder + ' 0 6px, transparent 6px 11px)' }} />
              </span>
              <div style={{ background: '#fff', border: `1.5px dashed ${OB.lockBorder}`, borderRadius: 10, padding: '9px 12px', margin: '6px 0 10px', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 800 }}>{s.name || s.short}</span>
                  <span style={{ fontSize: 12.5, color: OB.ink2 }}>{s.done} of {s.total} checkpoints done</span>
                </span>
                <BtnSoft onClick={() => window.open('https://calendly.com/wequote/onboarding-training-call', '_blank', 'noopener')}>Book</BtnSoft>
              </div>
            </div>
          )
        })}
      </Card>
    </div>
  )
}

/* ══ Notes & activity — CoNotes.dc.html ══ */
const NotesTab: React.FC<{ ob: any; deal: any; onChanged: () => void }> = ({ ob, deal, onChanged }) => {
  const [type, setType] = useState('Note')
  const [label, setLabel] = useState('None')
  const [kind, setKind] = useState('All')
  const [text, setText] = useState('')
  const acts: any[] = deal?.activities || []
  const stations = ((ob.stages || []) as any[]).filter((s) => !s.hidden).map((s) => s.short || s.name).slice(0, 5)
  const KIND_MAP: Record<string, (a: any) => boolean> = {
    All: () => true,
    Notes: (a) => a.type === 'note',
    Calls: (a) => a.type === 'call',
    Emails: (a) => a.type === 'email',
    System: (a) => a.type === 'system' || a.type === 'stage_change',
  }
  const shown = acts.filter(KIND_MAP[kind] || (() => true))
  const log = () => {
    if (!text.trim() || !ob.leadId) return
    const body = label !== 'None' ? `[${label}] ${text.trim()}` : text.trim()
    addActivity(ob.leadId, { type: type.toLowerCase() === 'note' ? 'note' : type.toLowerCase(), body } as any).then(() => { setText(''); crmToast(`${type} logged`, 'success'); onChanged() })
  }
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'hidden' }}>
      <Card style={{ flex: 'none' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['Note', 'Call', 'Email', 'Demo', 'WhatsApp'].map((t) => (
            <button key={t} onClick={() => setType(t)} style={{ font: 'inherit', fontSize: 13, borderRadius: 999, padding: '5px 12px', cursor: 'pointer', fontWeight: type === t ? 800 : 400, background: type === t ? OB.accent : OB.track, color: type === t ? '#fff' : OB.ink, border: `1px solid ${type === t ? OB.accent : OB.track}` }}>{t}</button>
          ))}
        </div>
        <textarea aria-label="Note" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" style={{ font: 'inherit', fontSize: 14, border: `1px solid ${OB.border}`, borderRadius: 10, padding: '10px 12px', minHeight: 70, resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Eyebrow>Label (optional)</Eyebrow>
            {['None', ...stations].map((l) => (
              <button key={l} onClick={() => setLabel(l)} style={{ font: 'inherit', fontSize: 12.5, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontWeight: label === l ? 800 : 400, background: label === l ? OB.purpleBg : '#fff', color: label === l ? OB.purple : OB.ink2, border: label === l ? `1.5px solid ${OB.purple}` : `1px solid ${OB.border}` }}>{l}</button>
            ))}
          </div>
          <BtnPrimary small onClick={log}>Log {type.toLowerCase()}</BtnPrimary>
        </div>
        <span style={{ fontSize: 12.5, color: OB.muted }}>Notes added from a station or a session are labelled automatically.</span>
      </Card>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.keys(KIND_MAP).map((k) => <Chip key={k} dark on={kind === k} onClick={() => setKind(k)} style={{ padding: '6px 13px' }}>{k}</Chip>)}
      </div>
      <Card gap={0} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {shown.slice(0, 80).map((a, i) => {
          const sys = a.type === 'system' || a.type === 'stage_change'
          const labelMatch = typeof a.body === 'string' ? a.body.match(/^\[([^\]]{1,30})\]\s/) : null
          const body = labelMatch ? a.body.slice(labelMatch[0].length) : a.body
          return (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderTop: `1px solid ${OB.rowDiv}`, alignItems: 'flex-start' }}>
              <span style={{ width: 30, height: 30, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, flex: '0 0 30px', background: sys ? OB.track : personColor(a.who, true), color: sys ? OB.muted : '#fff' }}>
                {sys ? 'SYS' : (a.who || '?').slice(0, 2).toUpperCase()}
              </span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{a.who || 'System'}</span>
                  <span style={{ fontSize: 12, color: OB.muted }}>{a.type}</span>
                  {labelMatch && <span style={{ fontSize: 11, fontWeight: 800, background: OB.purpleBg, color: OB.purple, borderRadius: 6, padding: '1px 7px' }}>{labelMatch[1]}</span>}
                </span>
                <span style={{ fontSize: 13.5, color: OB.ink2, whiteSpace: 'pre-wrap' }}>{body}</span>
              </div>
              <span style={{ fontSize: 12, color: OB.muted, whiteSpace: 'nowrap' }}>{a.at === 0 ? 'today' : `${a.at}d ago`}</span>
            </div>
          )
        })}
        {!shown.length && <span style={{ fontSize: 12.5, color: OB.faint, padding: '8px 0' }}>Nothing here.</span>}
      </Card>
    </div>
  )
}

/* ══ Emails & chats — CoEmails.dc.html chrome around the live components ══ */
const CommsTab: React.FC<{ ob: any; deal: any; ensureDraft: (k: string, w: string) => void }> = ({ ob, deal }) => {
  const navigate = useNavigate()
  const [ch, setCh] = useState<'email' | 'whatsapp' | 'chats' | 'support'>('email')
  const [drafts, setDrafts] = useState<any[]>([])
  useEffect(() => { if (ob.leadId) apiGet<any>(`/crm/ob-drafts?leadId=${ob.leadId}&status=to_send`).then((r: any) => setDrafts(r.drafts || r || [])).catch(() => {}) }, [ob.leadId])
  if (!ob.leadId) return <p style={{ fontSize: 12.5, color: OB.muted }}>No linked lead.</p>
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
      {drafts.length > 0 && (
        <section style={{ background: OB.amberPale, border: `1.5px solid ${OB.amberBorder}`, borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13.5, color: OB.amberInk }}><strong>{drafts.length} draft{drafts.length === 1 ? '' : 's'} waiting:</strong> {drafts[0].subject || drafts[0].type}</span>
          <BtnPrimary small onClick={() => navigate(`/crm/onboardings/followups?draft=${drafts[0].id}`)}>Review and send</BtnPrimary>
        </section>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {([['email', 'Email'], ['whatsapp', 'WhatsApp'], ['chats', 'Conversations'], ['support', 'Support']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setCh(k as any)} style={{ font: 'inherit', fontSize: 13.5, fontWeight: ch === k ? 800 : 400, borderRadius: 10, padding: '7px 16px', cursor: 'pointer', background: ch === k ? OB.accent : OB.surface, color: ch === k ? '#fff' : OB.ink, border: `1px solid ${ch === k ? OB.accent : OB.border}` }}>{l}</button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 16 }}>
        <div style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px' }}>
          {ch === 'email' && <DealEmails dealId={ob.leadId} />}
          {ch === 'whatsapp' && <DealWhatsApp dealId={ob.leadId} dealName={ob.companyName} />}
          {ch === 'chats' && <DealConvoHistory dealId={ob.leadId} />}
          {ch === 'support' && (deal ? <DealSupport deal={deal} /> : <p style={{ fontSize: 12.5, color: OB.muted }}>Loading…</p>)}
        </div>
      </div>
    </div>
  )
}

/* ══ Workflow and process (§2.11) — fills in with Map how you sell ══ */
const WorkflowTab: React.FC = () => (
  <Card style={{ maxWidth: 640, border: `1px dashed ${OB.inputBorder}`, textAlign: 'center', padding: 24 }}>
    <strong style={{ fontSize: 14 }}>Workflow and process</strong>
    <span style={{ fontSize: 13, color: OB.ink2 }}>The sales-process status, their sales line, output cards and the customer's map land here with the "Map how you sell" phase — being built next, nothing is lost in the meantime.</span>
  </Card>
)

/* ══ Plan & payments — CoPlan.dc.html ══ */
const PlanTab: React.FC<{ ob: any; deal: any; ensureDraft: (k: string, w: string) => void; navigate: (p: string) => void; setTab: (t: Tab) => void }> = ({ ob, deal, ensureDraft, navigate, setTab }) => {
  const over = ob.hoursAllowance ? Math.max(0, ob.hoursUsed - ob.hoursAllowance) : 0
  const priceStr = ob.price ? `£${Number(ob.price).toLocaleString('en-GB')}` : null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 15 }}>Plan hours</strong>
            <Pill bg={OB.track} fg={OB.ink} size={12.5} style={{ padding: '2px 10px' }}>{ob.plan || 'No plan'}{ob.hoursAllowance ? ` · ${ob.hoursAllowance}h` : ''}</Pill>
          </div>
          <span style={{ fontSize: 20, fontWeight: 800, color: over > 0 ? OB.red : OB.ink }}>{ob.hoursUsed}h used{over > 0 ? ` · ${over.toFixed(over % 1 ? 1 : 0)}h over` : ''}</span>
          {ob.hoursAllowance ? (
            <div style={{ height: 10, borderRadius: 5, overflow: 'hidden', display: 'flex', background: OB.track }}>
              <div style={{ width: `${Math.min(100, (Math.min(ob.hoursUsed, ob.hoursAllowance) / ob.hoursUsed) * 100)}%`, background: over > 0 ? OB.amberDot : OB.accent }} />
              {over > 0 && <div style={{ flex: 1, background: OB.red }} />}
            </div>
          ) : null}
          {over > 0 && <span style={{ fontSize: 12.5, color: OB.muted }}>{ob.overNotice ? `Over-plan notice sent ${fmtDay(ob.overNotice.at)}.` : 'The customer has not been told yet.'} Extra hours are chargeable.</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnPrimary small onClick={() => ensureDraft('plan_hours', `${ob.hoursUsed}h of ${ob.hoursAllowance || '—'}h used`)}>Notify the customer</BtnPrimary>
            <BtnOutline onClick={() => setTab('sessions')}>See hours by session</BtnOutline>
          </div>
        </Card>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: 15 }}>Contract</strong>
            {ob.leadId && <button onClick={() => navigate(`/crm/contracts/${ob.leadId}`)} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>Open in Contract manager ↗</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '8px 14px', fontSize: 13 }}>
            {([
              ['Training package', ob.plan ? `${ob.plan}${priceStr ? ` · ${priceStr}` : ''}` : 'Not set'],
              ['Payment', ob.paymentStatus === 'paid' ? 'Paid' : ob.paymentStatus === 'pending' ? 'Awaiting payment' : ob.paymentStatus || '—'],
              ['Go-live target', ob.goLiveDate ? fmtDay(ob.goLiveDate) : 'Not set'],
              ['Plan (platform)', deal?.plan || '—'],
            ] as Array<[string, string]>).map(([k, v]) => (
              <div key={k}><Eyebrow>{k}</Eyebrow><br /><span>{v}</span></div>
            ))}
          </div>
          <span style={{ fontSize: 12.5, color: OB.muted }}>Signed terms, schedule and payment stages live in the Contract Manager.</span>
        </Card>
      </div>
      <Card>
        <strong style={{ fontSize: 15 }}>Payments</strong>
        {([
          ob.price != null ? { what: `Onboarding (${ob.plan || 'plan'})`, when: 'on signing', amount: priceStr || '—', state: ob.paymentStatus === 'paid' ? 'Paid' : 'Awaiting', good: ob.paymentStatus === 'paid' } : null,
          over > 0 ? { what: 'Extra hours', when: `${over.toFixed(over % 1 ? 1 : 0)}h over ${ob.plan || 'plan'}`, amount: 'To agree', state: ob.overNotice ? 'Customer notified' : 'Not yet notified', good: false, red: !ob.overNotice } : null,
        ].filter(Boolean) as any[]).map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 0', borderTop: `1px solid ${OB.rowDiv}`, fontSize: 13.5 }}>
            <span style={{ flex: 1, minWidth: 0 }}><strong>{r.what}</strong> <span style={{ color: OB.muted }}>· {r.when}</span></span>
            <span style={{ fontWeight: 800 }}>{r.amount}</span>
            <Pill bg={r.good ? OB.greenBg : r.red ? OB.redBg : OB.track} fg={r.good ? OB.green : r.red ? OB.red : OB.muted} size={12}>{r.state}</Pill>
          </div>
        ))}
        {ob.price == null && over <= 0 && <span style={{ fontSize: 12.5, color: OB.faint }}>No payments on record.</span>}
      </Card>
      <Card>
        <strong style={{ fontSize: 15 }}>Quotes to this customer</strong>
        {(deal?.quotes || []).map((q: any) => (
          <span key={q.id} style={{ fontSize: 13, padding: '2px 0' }}>{q.num} · {q.status} · <strong>£{Number(q.total).toLocaleString('en-GB')}</strong></span>
        ))}
        {!(deal?.quotes || []).length && <span style={{ fontSize: 12.5, color: OB.faint }}>None</span>}
      </Card>
    </div>
  )
}

/* ══ Details — CoDetails.dc.html ══ */
const DetailsTab: React.FC<{ ob: any; deal: any; onChanged: () => void; navigate: (p: string) => void }> = ({ ob, deal, onChanged, navigate }) => {
  const [qualOpen, setQualOpen] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  useEffect(() => { getUsers().then((u: any) => setUsers(u.users || u)).catch(() => {}) }, [])
  if (!deal) return <p style={{ fontSize: 12.5, color: OB.muted }}>No linked lead — details unavailable.</p>
  const stages = Object.entries(STAGE_MAP).filter(([k]) => k !== 'setup_needed')
  const KV: React.FC<{ title: string; rows: Array<[string, React.ReactNode]> }> = ({ title, rows }) => (
    <Card gap={4}>
      <Eyebrow>{title}</Eyebrow>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13.5, padding: '4px 0' }}>
          <span style={{ color: OB.muted }}>{k}</span>
          <span style={{ textAlign: 'right', minWidth: 0 }}>{v}</span>
        </div>
      ))}
    </Card>
  )
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card row pad="12px 16px" style={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Eyebrow>Stage</Eyebrow>
            <span style={{ fontSize: 13, fontWeight: 800, background: OB.accent, color: '#fff', borderRadius: 999, padding: '4px 12px' }}>{STAGE_MAP[deal.stage]?.label || deal.stage}</span>
          </div>
          <select aria-label="Change stage" value="" onChange={(e) => { if (e.target.value && window.confirm(`Move ${deal.name} to ${STAGE_MAP[e.target.value]?.label}?`)) moveDealStage(deal.id, e.target.value).then(() => { crmToast('Stage changed', 'success'); onChanged() }) }}
            style={{ font: 'inherit', fontSize: 13, border: `1px solid ${OB.border}`, borderRadius: 8, padding: '6px 8px', background: '#fff' }}>
            <option value="">Change stage…</option>
            {stages.map(([k, s]) => <option key={k} value={k}>{(s as any).label}</option>)}
          </select>
        </Card>
        <KV title="Touchpoints" rows={[
          ['Email', deal.contactRecord?.email || '—'],
          ['Telephone', deal.contactRecord?.phone || deal.contactPhone || '—'],
          ['WhatsApp', deal.whatsappGroupName || (deal.whatsappGroupId ? 'linked' : 'not linked')],
        ]} />
        <KV title="Contact" rows={[['Name', deal.contact || '—'], ['Email', deal.contactRecord?.email || '—'], ['Phone', deal.contactRecord?.phone || deal.contactPhone || '—']]} />
        <KV title="Company" rows={[['Company', deal.name || ob.companyName], ['Industry', deal.industry || '—'], ['Country', deal.country || '—']]} />
        <KV title="Deal / commercials" rows={[['Source', deal.source || 'not set'], ['MRR', deal.mrr ? `£${Number(deal.mrr).toLocaleString('en-GB')}` : '—'], ['Plan', deal.plan || '—'], ['Owner', users.find((u) => String(u.id) === String(deal.ownerId))?.name || 'Unassigned']]} />
        <KV title="WeQuote account" rows={[
          ['Account ID', deal.wequoteAccountId ? `${deal.wequoteAccountId}` : 'not linked'],
          ['Lifecycle status', deal.lifecycleStatus || '—'],
          ['Agreed go-live', ob.goLiveDate ? new Date(ob.goLiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'not set'],
        ]} />
      </div>
      <aside style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {deal.score != null && (
          <Card gap={6}>
            <Eyebrow>Lead score</Eyebrow>
            <div style={{ height: 8, borderRadius: 4, background: OB.rowDiv, overflow: 'hidden' }}><div style={{ height: '100%', width: `${Math.min(100, deal.score)}%`, background: OB.amberDot }} /></div>
            <span style={{ fontSize: 13, fontWeight: 800, color: OB.amber }}>{deal.score}</span>
          </Card>
        )}
        <Card gap={6}>
          <Eyebrow>Qualification</Eyebrow>
          <button onClick={() => setQualOpen(!qualOpen)} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}>{qualOpen ? 'Close qualification panel' : 'Open qualification panel →'}</button>
        </Card>
        <Card gap={6}>
          <Eyebrow>Manage</Eyebrow>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <BtnOutline onClick={() => navigate(`/crm/lead/${deal.id}`)}>Open lead screen</BtnOutline>
            <BtnOutline onClick={() => { if (window.confirm(`Archive the onboarding for ${ob.companyName}?`)) obArchive(ob.id, true).then(() => { crmToast('Archived', 'success'); navigate('/crm/onboardings') }) }}>Archive</BtnOutline>
          </div>
          <span style={{ fontSize: 12.5, color: OB.muted }}>Moved here from the header so the onboarding tabs stay clean.</span>
        </Card>
      </aside>
      {qualOpen && <div style={{ flexBasis: '100%', background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px' }}><DealQualification dealId={deal.id} /></div>}
    </div>
  )
}

export default CrmObCompanyPage
